import React from 'react'
import { inject, observer } from 'mobx-react'
import { Icon, Tag } from 'antd'
import { CreateProduct, CreateChildProduct } from '../../ProductStore'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { CurrencyInput } from '../../../../../../components/CurrencyInput'
import { Currency } from '../../../../../../components/Currency'
import { EditPurchasePriceModal } from './EditPurchasePriceModal'
import { Routes } from '../../../../../../constants/Routes'
import { MultiLocationPrices } from './MultiLocationPrices'
import { ResponsiveRow } from '../../../../../../components/ResponsiveContainer/ResponsiveRow'
import { Checkbox, Button, Row } from 'antd-v4'
import { RowProps } from 'antd/lib/row'
import { ColProps } from 'antd/lib/col'
import { PercentageInput } from '../../../../../../components/Input/PercentageInput'
import { DiscountModels } from '@getgreenline/products'

interface Props<T extends CreateProduct | CreateChildProduct> {
  product: T
  currentCompanyStore?: CurrentCompanyStore
  saveProductFirstWarning: T extends CreateProduct ? JSX.Element : undefined
}

interface State {
  showEditPurchaseCostModal: boolean
  isOverrideWeightedAverage: boolean
  showProductDiscount?: DiscountModels.IDiscount
  showBottleDepositFeeInput: boolean
}

@inject('currentCompanyStore')
@observer
export class PricingTab<T extends CreateProduct | CreateChildProduct> extends React.Component<
  Props<T>,
  State
> {
  constructor(props: Props<T>) {
    super(props)
    this.state = {
      showEditPurchaseCostModal: false,
      isOverrideWeightedAverage: false,
      showBottleDepositFeeInput: !!this.props.product.depositFee,
    }
  }

  render() {
    const { product, saveProductFirstWarning } = this.props
    const currentCompanyStore = this.props.currentCompanyStore!
    const { showEditPurchaseCostModal, isOverrideWeightedAverage, showBottleDepositFeeInput } =
      this.state
    const company = currentCompanyStore.company!
    const rowProps: RowProps = { style: { marginBottom: 16 } }
    const labelColProps: ColProps = { md: 18, lg: 12 }
    const contentColProps: ColProps = { md: 16, lg: 12 }

    return (
      <div>
        <ResponsiveRow
          labelText={
            <div>
              <strong>Retail price</strong>
              <div className='text-muted'>The price your product will be sold at</div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          {product instanceof CreateProduct && product.needsPricingGroupRefresh ? (
            <div>{saveProductFirstWarning}</div>
          ) : (
            <div>
              {product.isFreePricing ? (
                <span className='text-muted'>Uncheck "Free pricing" to set this value</span>
              ) : (
                <CurrencyInput
                  className='gr-currency-input mr-3'
                  centValue={product.price}
                  onlyPositiveNumber
                  disabled={product.isDeleted}
                  onChange={(newValue) => product.setPrice(newValue)}
                />
              )}

              {product instanceof CreateProduct && (
                <Checkbox
                  className='mt-1'
                  checked={product.isFreePricing}
                  disabled={product.isDeleted || product.isGiftCard}
                  onChange={(e) => {
                    const newPrice = e.target.checked ? null : 0
                    product.setPrice(newPrice)
                  }}
                >
                  Free pricing
                </Checkbox>
              )}
            </div>
          )}
        </ResponsiveRow>

        <ResponsiveRow
          labelText={
            <div>
              <strong>Wholesale cost</strong>
              <div className='text-muted'>
                <span>Automatically filled when you make a </span>
                <a href={Routes.DASHBOARD_INVOICES(company.id)} target='_blank' rel='noreferrer'>
                  purchase order
                </a>
              </div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          {company.inventoryCostOption === 'weightedAverage' && !isOverrideWeightedAverage && (
            <Button
              disabled={product.isDeleted}
              onClick={() =>
                this.setState({ showEditPurchaseCostModal: !showEditPurchaseCostModal })
              }
            >
              <Currency centValue={product.purchasePrice} />
            </Button>
          )}

          {(company.inventoryCostOption === 'lastCost' || isOverrideWeightedAverage) && (
            <CurrencyInput
              className='gr-currency-input'
              centValue={product.purchasePrice}
              onlyPositiveNumber
              disabled={product.isDeleted}
              onChange={(newValue) => product.setPurchasePrice(newValue)}
            />
          )}

          <EditPurchasePriceModal
            visible={showEditPurchaseCostModal}
            onHide={() =>
              this.setState({
                showEditPurchaseCostModal: false,
                isOverrideWeightedAverage: true,
              })
            }
          />
        </ResponsiveRow>

        <ResponsiveRow
          labelText={
            <div>
              <span className='d-flex'>
                <strong>Margin</strong>
                {product.margin < 0 && (
                  <Tag className='ml-2' color='red'>
                    Negative
                  </Tag>
                )}
              </span>
              <div className='text-muted'>
                (price - cost) / <strong>price</strong> * 100
              </div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          <PercentageInput
            disabled={product.isGiftCard}
            max={100}
            value={product.margin}
            onChange={(margin) => {
              product.setMargin(Number(margin))
            }}
          />
        </ResponsiveRow>

        <ResponsiveRow
          labelText={
            <div>
              <span className='d-flex'>
                <strong>Markup</strong>
                {product.markup < 0 && (
                  <Tag className='ml-2' color='red'>
                    Negative
                  </Tag>
                )}
              </span>
              <div className='text-muted'>
                (price - cost) / <strong>cost</strong> * 100
              </div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          <PercentageInput
            disabled={product.isGiftCard}
            value={product.markup}
            onChange={(markup) => {
              product.setMarkup(Number(markup))
            }}
          />
        </ResponsiveRow>

        {showBottleDepositFeeInput ? (
          <ResponsiveRow
            labelText={
              <div>
                <div>
                  <strong>Bottle deposit fee</strong>
                  <Button
                    size='small'
                    className='ml-2'
                    onClick={() => {
                      product.setDepositFee(null)
                      this.setState({ showBottleDepositFeeInput: !showBottleDepositFeeInput })
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className='text-muted'>
                  Add a non-taxed deposit fee to your bottles. Used for cannabis beverages in
                  certain provinces.
                </div>
              </div>
            }
            rowProps={rowProps}
            labelColProps={labelColProps}
            contentColProps={contentColProps}
          >
            <CurrencyInput
              className='gr-currency-input'
              centValue={product.depositFee}
              onlyPositiveNumber
              onChange={(fee) => product.setDepositFee(fee)}
            />
          </ResponsiveRow>
        ) : (
          <ResponsiveRow
            labelText={
              <Button
                onClick={() =>
                  this.setState({
                    showBottleDepositFeeInput: !showBottleDepositFeeInput,
                  })
                }
                disabled={product.isDeleted}
              >
                <Icon type='plus' />
                <span>Add deposit fee</span>
              </Button>
            }
            rowProps={rowProps}
            labelColProps={labelColProps}
            contentColProps={contentColProps}
          />
        )}

        <Row>
          <MultiLocationPrices product={product} />
        </Row>
      </div>
    )
  }
}
